import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

// Define the PetInfo interface used in user metadata
interface PetInfo {
  id: string;
  name: string;
  type: string;
  birthday?: string;
  weight?: number;
  photo_url?: string;
}

// Define the UserContext type
interface UserContextType {
  user: any | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ error: any | null }>;
  signUpWithEmail: (data: { email: string; password: any; fullName: string; pets: PetInfo[] }) => Promise<{ error: any | null }>;
  updateProfile: (updates: any) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * The UserProvider component that manages authentication state and profile updates.
 */
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    const checkSession = async () => {
      try {
        const { data: { session } } = await (supabase.auth as any).getSession();
        setUser(session?.user ?? null);
      } catch(e) {
        console.warn("Auth session check failed", e);
      }
      setLoading(false);
    };
    
    checkSession();

    const { data: { subscription } } = (supabase.auth as any).onAuthStateChange((_event: any, session: any) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    if (!isSupabaseConfigured) return { error: { message: "Mock Mode: Auth disabled" } };
    const { data, error } = await (supabase.auth as any).signInWithPassword({ email, password });
    return { error };
  };

  const signUpWithEmail = async (data: { email: string; password: any; fullName: string; pets: PetInfo[] }) => {
    if (!isSupabaseConfigured) return { error: { message: "Mock Mode: Auth disabled" } };
    const { email, password, fullName, pets } = data;
    const { data: authData, error } = await (supabase.auth as any).signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          pets: pets,
          joined_at: new Date().toISOString()
        }
      }
    });
    return { error };
  };

  const updateProfile = async (updates: any) => {
    if (!isSupabaseConfigured) return { error: { message: "Mock Mode: Auth disabled" } };
    const { data, error } = await (supabase.auth as any).updateUser({
      data: updates
    });
    if (!error && data.user) {
      setUser(data.user);
    }
    return { error };
  };

  const signOut = async () => {
    if (isSupabaseConfigured) {
      await (supabase.auth as any).signOut();
    }
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, loading, signInWithEmail, signUpWithEmail, updateProfile, signOut }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
